import { defineStore } from "pinia";

import tripCargosApi from "@/services/api/tripCargosApi";

import { getCompanyId } from "@/utils/userUtils";

export const useTripCargoStore = defineStore("tripCargoStore", {
  state: () => ({
    cargos: [],
    companyId: getCompanyId(),
    isLoading: false,
    error: null,
    searchQuery: "",

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },
  }),

  actions: {
    async fetchCargos(tripId, payload = {}) {
      this.searchQuery = payload.search || "";
      return await this.getCargosRequest({ tripId, page: 1 });
    },

    async fetchOrderCargos(orderId) {
      this.searchQuery = "";
      return await this.getCargosRequest({
        "q[order_id]": orderId,
        page: 1,
      });
    },

    async loadMoreCargos(tripId) {
      if (this.isLoading || !this.pagination.next) return;
      const nextPage = this.pagination.page + 1;
      return await this.getCargosRequest({
        tripId,
        page: nextPage,
        append: true,
      });
    },

    async getCargosRequest({
      tripId,
      page = 1,
      append = false,
      ...extraParams
    } = {}) {
      const pageNumber = parseInt(page) || 1;
      this.isLoading = true;
      try {
        const params = { page: pageNumber, ...extraParams };

        if (tripId) {
          params["q[trip_id]"] = tripId;
        }
        if (this.searchQuery) {
          params["q[search]"] = this.searchQuery;
        }

        const response = await tripCargosApi.index(
          this.companyId,
          params
        );
        const result = response.data.result || response.data || [];

        if (append) {
          this.cargos = [...this.cargos, ...result];
        } else {
          this.cargos = result;
        }

        const meta = response.data.pagination_meta;
        if (meta) {
          this.pagination = {
            page: meta.page,
            pages: meta.pages,
            series: meta.series ?? [],
            next: meta.next,
            prev: meta.prev,
            count: meta.count,
            limit: meta.limit,
          };
        }
      } catch (error) {
        this.error = error;
        console.error("Ошибка при получении грузов:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async showCargo(id) {
      try {
        const response = await tripCargosApi.show(this.companyId, id);
        return response.data.result || response.data || {};
      } catch (error) {
        this.error = error;
        console.error("Ошибка при получении груза:", error);
      }
    },

    async createCargo(payload, params = {}) {
      this.isLoading = true;
      try {
        const response = await tripCargosApi.create(
          this.companyId,
          payload
        );
        await this.getCargosRequest(
          Object.keys(params).length ? params : { tripId: payload.trip_id }
        );
        return response.data.result || response.data || {};
      } catch (error) {
        console.error("Ошибка при создании груза:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateCargo(id, payload, params = {}) {
      this.isLoading = true;
      try {
        const response = await tripCargosApi.update(
          this.companyId,
          id,
          payload
        );
        await this.fetchCargos(payload.trip_id, params);
        return response.data.result || response.data || {};
      } catch (error) {
        console.error("Ошибка при обновлении груза:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async deleteCargo(id, tripId, params = {}) {
      this.isLoading = true;
      try {
        await tripCargosApi.delete(this.companyId, id);
        await this.fetchCargos(tripId, params);
      } catch (error) {
        console.error("Ошибка при удалении груза:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
